import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ConfigContext } from '../shared/ConfigContext';
import AdvertisementClient from '../../api/advertisementClient';
import { usePlanLimitContent } from '../help/planLimitContent';

const PAGE_SIZE = 25;

// Same accumulating "load more" fetch pattern as useCouponsPage/useCategoriesPage.
// Status is a real stored field so it stays a server-side param (unlike
// Coupons' derived active/expired), matching Invoices' status-chip precedent.
const useAdvertisementsPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');
  const planLimit = usePlanLimitContent('advertisements.active.max');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchAdvertisements = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const filters = { page, pageSize: PAGE_SIZE };
    if (search) filters.search = search;
    if (statusFilter !== 'all') filters.status = statusFilter;
    const client = new AdvertisementClient(config.backendUrl);
    client.getAdvertisements(companyId, filters)
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching advertisements', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /admin/advertisements · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, search, statusFilter, t]);

  useEffect(() => { fetchAdvertisements(true); }, [fetchAdvertisements]);

  const loadMore = () => fetchAdvertisements(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const openCreateWizard = () => {
    setEditingAd(null);
    setWizardOpen(true);
  };

  const openEditWizard = (ad) => {
    setEditingAd(ad);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setEditingAd(null);
  };

  const handleWizardSuccess = (message) => {
    setWizardOpen(false);
    setEditingAd(null);
    notify(message);
    fetchAdvertisements(true);
  };

  const openDeleteConfirm = (ad) => {
    setConfirmTarget(ad);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      const client = new AdvertisementClient(config.backendUrl);
      await client.deleteAdvertisement(confirmTarget._id);
      notify(t('Advertisement deleted successfully'));
      setConfirmOpen(false);
      fetchAdvertisements(true);
    } catch (error) {
      console.error('Error deleting advertisement', error);
      notify(t('Failed to delete advertisement'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDuplicate = async (ad) => {
    try {
      const client = new AdvertisementClient(config.backendUrl);
      await client.duplicateAdvertisement(ad._id);
      notify(t('Advertisement duplicated successfully'));
      fetchAdvertisements(true);
    } catch (error) {
      console.error('Error duplicating advertisement', error);
      notify(t('Failed to duplicate advertisement'), 'error');
    }
  };

  const handleToggleActive = async (ad) => {
    try {
      const client = new AdvertisementClient(config.backendUrl);
      if (ad.status === 'active') {
        await client.deactivateAdvertisement(ad._id);
        notify(t('Advertisement deactivated successfully'));
      } else {
        await client.activateAdvertisement(ad._id);
        notify(t('Advertisement activated successfully'));
      }
      fetchAdvertisements(true);
    } catch (error) {
      console.error('Error toggling advertisement status', error);
      notify(ad.status === 'active' ? t('Failed to deactivate advertisement') : t('Failed to activate advertisement'), 'error');
    }
  };

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search || statusFilter !== 'all');

  return {
    t, companyId, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, setSearch, statusFilter, setStatusFilter,
    loadingMore, loadMore, remaining, nextBatchSize, fetchAdvertisements,
    wizardOpen, editingAd, openCreateWizard, openEditWizard, closeWizard, handleWizardSuccess,
    confirmOpen, setConfirmOpen, confirmTarget, deleting, openDeleteConfirm, handleConfirmDelete,
    handleDuplicate, handleToggleActive,
    snackbar, setSnackbar,
    planLimit,
  };
};

export default useAdvertisementsPage;
